import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { HotelSearchValue } from '@voll-intelligence/intelligence-ui-kit';
import {
  HOTEL_MAX_GUESTS_PER_ROOM,
  type ChatUiHotelOffer,
  type HotelPlannerBlock,
  type HotelPlannerSelection,
} from '@voll-intelligence/genui-contract';
import { chosenPlace, draftSelection, selectionIsSettled } from './hotel_selection.js';
import { createHotelPlannerView } from './hotel_planner_view.js';
import { withDeterministicOffers } from './hotel_results_handlers.js';
import { useHotelAutoSearch } from './use_hotel_auto_search.js';
import { hasDetailsPort, type HotelResultsConfig } from './planner_panel_list.js';
import { useExpiredSearchRetry } from './use_expired_search_retry.js';
import type { useHotelSearchOptions } from './use_hotel_search_options.js';

/** Destino escolhido FORA do formulário (os atalhos de recentes/favoritos da
 *  página de busca). Escreve lugar e `placeId` no rascunho — e, quando o
 *  atalho carrega a JANELA da busca original (`checkin`/`checkout` completos,
 *  `guests`), também ela: repetir uma busca recente é repetir o período e a
 *  ocupação, não só o destino. O que o atalho NÃO traz fica de pé, que é a
 *  diferença entre isto e remontar o formulário com uma `key` nova.
 *
 *  CONTRATO: identidade nova = ESCOLHA nova. O host tem de guardar o objeto
 *  em estado e criar um novo A CADA gesto (mesmo repetindo o lugar) — um
 *  literal montado no JSX reaplicaria o destino a cada render, atropelando a
 *  digitação de quem trocou de ideia.
 *
 *  O `placeId` só vira coordenada se o campo já conhecer o lugar; ele conhece,
 *  porque o atalho e o popover leem a MESMA rota de sugestões. Sem `placeId`
 *  (os cartões de afinidade da home, que só sabem a CIDADE em texto), o
 *  rascunho vira busca por texto — o BFF resolve o lugar, como numa digitação. */
export type HotelPlacePrefill = {
  place: string;
  placeId?: string;
  checkin?: string;
  checkout?: string;
  guests?: number;
};

/** O aberto/fechado de uma tela em diálogo do organismo (a lista, o mapa
 *  cheio). Foco, trap, Esc e a devolução do foco são do Base UI
 *  (`ScreenDialog`); aqui só o estado e o aviso ao host quando o card remonta
 *  com ela aberta. */
function useResultsDialogLifecycle(onRemount?: () => void, initialExpanded = false) {
  const [expanded, setExpanded] = useState(initialExpanded);
  const expandedRef = useRef(initialExpanded);
  const dialogRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => {
    expandedRef.current = false;
    setExpanded(false);
  }, []);
  const open = useCallback(() => {
    expandedRef.current = true;
    setExpanded(true);
  }, []);

  useEffect(
    () => () => {
      if (expandedRef.current) onRemount?.();
    },
    [onRemount],
  );

  return { expanded, dialogRef, open, close };
}

/** Aplica ao rascunho um destino escolhido fora do formulário.
 *
 *  A comparação é por IDENTIDADE do objeto, e o contrato que ela impõe ao host
 *  está na doc da prop: identidade nova = ESCOLHA nova. Não por `placeId`
 *  porque escolher o MESMO atalho de novo é um gesto legítimo — depois de
 *  digitar outro destino à mão, é assim que se volta para ele —, e uma guarda
 *  por valor deixava esse segundo clique sem efeito nenhum.
 *
 *  `selectedPlaceId` é escrito SEMPRE (mesmo `undefined`): um prefill só-texto
 *  precisa apagar a coordenada do lugar anterior, senão a busca sairia com o
 *  texto novo e o pino velho. A janela e a ocupação só entram quando o atalho
 *  as traz E quando ainda cabem no formulário:
 *  - par completo de datas (regra do calendário) e SÓ no futuro — o atalho
 *    sobrevive semanas na LRU, e um calendário pré-preenchido com datas
 *    vencidas só fabrica uma busca que o upstream rejeita;
 *  - `guests` vira UM quarto com a ocupação total (a única forma que um total
 *    recuperado do histórico sabe assumir), mas só até o teto por quarto —
 *    um total maior não cabe em quarto válido nenhum e travaria o submit
 *    (`createSelection` devolve null). Fora das guardas, o campo fica como
 *    está; o destino pré-preenche sempre. */
function usePlacePrefill(
  placePrefill: HotelPlacePrefill | undefined,
  setSearchValue: (update: (current: HotelSearchValue) => HotelSearchValue) => void,
): void {
  useEffect(() => {
    if (!placePrefill) return;
    const today = new Date().toISOString().slice(0, 10);
    const window =
      placePrefill.checkin && placePrefill.checkout && placePrefill.checkin >= today
        ? { checkin: placePrefill.checkin, checkout: placePrefill.checkout }
        : {};
    const rooms =
      placePrefill.guests && placePrefill.guests <= HOTEL_MAX_GUESTS_PER_ROOM
        ? { rooms: [{ guests: placePrefill.guests }] }
        : {};
    setSearchValue((current) => ({
      ...current,
      ...chosenPlace(placePrefill.place, placePrefill.placeId),
      ...window,
      ...rooms,
    }));
  }, [placePrefill, setSearchValue]);
}

/** A view do planner, memoizada — extraída do organismo pelo teto de linhas
 *  por função. `offers` são as ofertas da página da bancada, fora do bloco (a
 *  conversa não as passa). O `effectiveUi` (objeto novo a cada render enquanto
 *  `offers` estiver setada) NÃO entra nas deps de propósito: o memo recomputa
 *  seu PRÓPRIO `withDeterministicOffers(ui, offers)` dentro do callback,
 *  dependendo só de `ui`/`offers` (referências estáveis do host). */
export function usePlannerView(input: {
  readonly ui: HotelPlannerBlock;
  readonly offers?: readonly ChatUiHotelOffer[] | undefined;
  readonly config: HotelResultsConfig;
  readonly locale: string;
}): ReturnType<typeof createHotelPlannerView> {
  const { ui, offers, config, locale } = input;
  // A porta de detalhe é do config: quem a lê é a view, não o organismo.
  const detailsAvailable = hasDetailsPort(config);
  return useMemo(
    () =>
      createHotelPlannerView(withDeterministicOffers(ui, offers), {
        locale,
        mapboxAccessToken: config.mapboxAccessToken,
        defaultMapLocation: config.hotelMapDefaultLocation,
        proxyImageUrl: config.proxyImageUrl,
        detailsAvailable,
      }),
    [
      config.hotelMapDefaultLocation,
      config.mapboxAccessToken,
      config.proxyImageUrl,
      detailsAvailable,
      locale,
      ui,
      offers,
    ],
  );
}

/** A seleção estruturada corrente (destino + estadia + hóspedes + filtros),
 *  derivada do rascunho do form + do que o bloco já trouxe. `null` enquanto
 *  faltar campo obrigatório — é o que trava o submit e o auto-search. */
export function useHotelPlannerSelection(
  ui: HotelPlannerBlock,
  options: ReturnType<typeof useHotelSearchOptions>,
  searchValue: HotelSearchValue,
): HotelPlannerSelection | null {
  const draft = options.resolveValue(searchValue);
  return useMemo(() => draftSelection(ui, draft), [draft, ui]);
}

export function useHotelSearchSubmission({
  ui,
  selection,
  locked,
  superseded,
  autoSearch,
  expired,
  onSearch,
  onRetry,
  onTurnSearch,
  selectionOf,
}: {
  /** O bloco com as ofertas EFETIVAS (`withDeterministicOffers`), não o de fio:
   *  `selectionIsSettled` lê `ui.offers` cru, então com o bloco truncado a prop
   *  determinística nunca assentaria "já buscou" e a auto-busca (se um consumidor
   *  futuro ligar `autoSearch`) dispararia espúria sobre a página já cheia. */
  readonly ui: HotelPlannerBlock;
  readonly selection: HotelPlannerSelection | null;
  readonly locked: boolean;
  /** Card superado: refinar deixa de ser leitura e vira turno. */
  readonly superseded: boolean;
  readonly autoSearch: boolean;
  /** A lista completa acusou busca vencida? Ver `useExpiredSearchRetry`. */
  readonly expired: boolean;
  readonly onSearch: (selection: HotelPlannerSelection) => Promise<void>;
  readonly onRetry: (selection?: HotelPlannerSelection) => Promise<void>;
  readonly onTurnSearch: (selection: HotelPlannerSelection, value?: HotelSearchValue) => void;
  /** A seleção de um valor da barra — a busca leva o valor que ela entrega. */
  readonly selectionOf: (value: HotelSearchValue) => HotelPlannerSelection | null;
}) {
  const settled = selectionIsSettled(ui, false);
  const [submittedChallengeId, setSubmittedChallengeId] = useState<string | null>(() =>
    settled ? ui.challengeId : null,
  );
  // Um bloco que já chega buscado conta como submetido — ajustado no render.
  if (settled && submittedChallengeId !== ui.challengeId) setSubmittedChallengeId(ui.challengeId);
  const runSearch = useCallback(
    (next: HotelPlannerSelection) => {
      setSubmittedChallengeId(ui.challengeId);
      return onSearch(next);
    },
    [onSearch, ui.challengeId],
  );

  // O auto-search com debounce é uma LEITURA: só vale no card que ainda é o
  // último da conversa. Superado, cada tecla viraria uma mensagem — aí a busca
  // espera o gesto explícito no "Buscar".
  useHotelAutoSearch({
    selection,
    settled,
    interactionId: ui.challengeId,
    disabled: !autoSearch || locked || superseded || submittedChallengeId === ui.challengeId,
    onSearch: runSearch,
  });

  const expiredRetry = useExpiredSearchRetry({
    expired,
    sessionId: ui.sessionId,
    selection,
    disabled: locked,
    onSearch,
  });

  return {
    expiredRetry,
    canSubmit: (value: HotelSearchValue) => selectionOf(value) !== null,
    submit: (value: HotelSearchValue) => {
      const next = selectionOf(value);
      if (!next || locked) return;
      if (superseded) onTurnSearch(next, value);
      else void runSearch(next);
    },
    retry: () => {
      if (!selection || locked) return;
      if (superseded) onTurnSearch(selection);
      else void onRetry(selection);
    },
  };
}

/** `fullscreenMap` (mapa cheio) e `allHotels` (lista cheia da conversa)
 *  compartilham o mesmo hook de ciclo de vida.
 *
 *  M1 · 1.2 — `initialOpen` (`ui.directives.open`) nasce com uma das duas já
 *  expandida: o agente respondeu um refino pedindo "abre o mapa"/"mostra
 *  todos" e o card reemitido já entra assim. Como o card remonta o organismo
 *  por `challengeId#epoch` (`HotelPlannerCard`), um bloco reemitido com `open`
 *  abre uma vez por emissão — o comportamento desejado, não um estado preso. */
export function useHotelResultsDialogs(
  onExpandedRemount: (() => void) | undefined,
  initialOpen?: 'panel' | 'map',
) {
  const fullscreenMap = useResultsDialogLifecycle(onExpandedRemount, initialOpen === 'map');
  const allHotels = useResultsDialogLifecycle(onExpandedRemount, initialOpen === 'panel');
  // As duas superfícies que leem a lista completa. Uma aberta basta: o mapa
  // cheio abre por cima da lista, e também direto do card.
  return {
    fullscreenMap,
    allHotels,
    surfacesOpen: allHotels.expanded || fullscreenMap.expanded,
  };
}

/** O rascunho do formulário de busca, semeado pela view e re-semeado pelo
 *  `placePrefill` (ver `HotelPlacePrefill`). */
export function useSearchValueState(
  initial: HotelSearchValue,
  placePrefill: HotelPlacePrefill | undefined,
) {
  const [value, setValue] = useState(initial);
  usePlacePrefill(placePrefill, setValue);
  return [value, setValue] as const;
}
